import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { CreditsExhaustionContext } from "@/components/QueryClientProvider";
import { TrackLocations } from "@/hooks/use-segment";
import { ExternalLinkIcon } from "lucide-react";
import { useCallback, useContext, type FC } from "react";
import { useNavigate } from "react-router-dom";

export const CreditsExhaustionModal: FC = () => {
    const { isCreditsExhausted, setCreditsExhaustion, onCloseCallback, clearCallback } =
        useContext(CreditsExhaustionContext);

    const navigate = useNavigate();

    const handleVisibilityChange = useCallback(
        (open: boolean) => {
            if (!open) {
                setCreditsExhaustion(false);
                if (onCloseCallback) {
                    onCloseCallback();
                    clearCallback();
                }
            }
        },
        [setCreditsExhaustion, onCloseCallback, clearCallback],
    );

    const handleViewBilling = useCallback(() => {
        navigate("/settings/billing");
        handleVisibilityChange(false);
    }, [handleVisibilityChange, navigate]);

    return (
        <Dialog open={isCreditsExhausted} onOpenChange={handleVisibilityChange}>
            <DialogContent className="max-w-md" data-track-location={TrackLocations.CreditsExhaustionModal}>
                <DialogHeader className="mb-1.5">
                    <DialogTitle>Out of Credits</DialogTitle>
                </DialogHeader>
                <DialogDescription>
                    Your organization has run out of credits and cannot perform this action. Add more credits to
                    continue using Ona.
                </DialogDescription>
                <DialogFooter>
                    <Button
                        onClick={() => handleVisibilityChange(false)}
                        variant="outline"
                        data-tracking-id="not-now-credit-exhaustion-modal"
                    >
                        Not now
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleViewBilling}
                        className="flex items-center gap-2"
                        data-tracking-id="view-billing-credit-exhaustion-modal"
                    >
                        View Billing
                        <ExternalLinkIcon className="size-4" />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
